import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { FileConnection } from '../../models/appcloud/file-connection.model';
import { TestConnectionResponse } from '../../models/appcloud/test-connection-response.model';
import { CsvHeaderResponse } from '../../models/appcloud/csv-header-response.model';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';

type FileListResponse = {
    status: string;
    message: string;
    files: {
        name: string;
        modifiedDateTime: Date;
    }[];
};

@Injectable({
    providedIn: 'root',
})
export class FileProcessorService {
    readonly #http = inject(HttpClient);
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #spinnerService = inject(SpinnerService);

    private BASE_URL = environment.publicApiHost + '/appcloud/file-processor';

    private HEADER_URL = this.BASE_URL + '/csv-headers';
    private HEADER_POLLING_URL = this.BASE_URL + '/csv-headers-polling';
    private TEST_CONNECTION_URL = this.BASE_URL + '/test-connection';

    private FILE_LIST_URL = this.BASE_URL + '/file-list/';

    getFileHeaders(fileConnection: FileConnection, additionalHeaders: any = {}): Observable<CsvHeaderResponse> {
        this.#spinnerService.addProcess('FileProcessorService.getFileHeaders', 'Getting file headers...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http.post<CsvHeaderResponse>(this.HEADER_URL, fileConnection, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving headers from your file. Please try again later.'
                );
                return of<CsvHeaderResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess('FileProcessorService.getFileHeaders'))
        );
    }

    getFileHeadersPolling(fileConnection: FileConnection, additionalHeaders: any = {}): Observable<CsvHeaderResponse> {
        const processName = 'FileProcessorService.getFileHeadersPollable';
        this.#spinnerService.addProcess(processName, 'Getting file headers...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http.post<CsvHeaderResponse>(this.HEADER_POLLING_URL, fileConnection, httpOptions).pipe(
            catchError((error: HttpErrorResponse) => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving headers from your file. Please try again later.'
                );
                return of<CsvHeaderResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(processName))
        );
    }

    testConnection(fileConnection: FileConnection, additionalHeaders: any = {}): Observable<TestConnectionResponse> {
        this.#spinnerService.addProcess('FileProcessorService.testConnection', 'Testing SFTP connection...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<TestConnectionResponse>(this.TEST_CONNECTION_URL, fileConnection, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while testing your SFTP connection. Please try again later.'
                );
                return of<TestConnectionResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess('FileProcessorService.testConnection'))
        );
    }

    getFileList(
        connection: {
            credentialedFileConnection: Pick<
                FileConnection,
                'host' | 'filePath' | 'port' | 'serverTechnology' | 'credentialId'
            >;
            afterDate: Date;
            beforeDate: Date;
            afterDateInclusive: boolean;
        },
        additionalHeaders: any = {}
    ): Observable<FileListResponse> {
        const processName = 'FileProcessorService.getFileList';
        this.#spinnerService.addProcess(processName, 'Getting file list ...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<FileListResponse>(this.FILE_LIST_URL, connection, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving file paths. Please try again later.'
                );
                return of<FileListResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(processName))
        );
    }
}
